import React, { useState, useEffect, useCallback } from 'react';
import { ipWhitelistAdminAPI, ipSecurityAPI, userAPI } from '../../services/api';
import * as Icons from 'lucide-react';

const IPWhitelistAdmin = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [stats, setStats] = useState({});
  const [msg, setMsg] = useState({ type: '', text: '' });

  const fetchStats = useCallback(async () => {
    try {
      const res = await ipSecurityAPI.getStats();
      setStats(res.data || {});
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const tabs = [
    { key: 'requests', label: 'IP Requests', icon: Icons.FileCheck, count: stats.pending_requests },
    { key: 'global_blacklist', label: 'Global Blacklist', icon: Icons.ShieldBan, count: stats.global_blacklist },
    { key: 'global_whitelist', label: 'Global Whitelist', icon: Icons.ShieldCheck, count: stats.global_whitelist },
    { key: 'country_rules', label: 'Country Rules', icon: Icons.Globe, count: (stats.country_blacklist || 0) + (stats.country_whitelist || 0) },
    { key: 'user_blacklist', label: 'User Blacklist', icon: Icons.UserX, count: stats.user_blacklist },
    { key: 'audit_log', label: 'Audit Log', icon: Icons.ScrollText },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2.5 bg-indigo-100 rounded-xl"><Icons.Shield className="w-6 h-6 text-indigo-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">IP Security & Whitelist Management</h1>
            <p className="text-sm text-gray-500">Manage IP approvals, blacklists, whitelists, and country-level rules</p>
          </div>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 p-3 rounded-lg border text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg.type === 'success' ? <Icons.CheckCircle className="w-4 h-4" /> : <Icons.AlertCircle className="w-4 h-4" />}
          {msg.text}
          <button onClick={() => setMsg({ type: '', text: '' })} className="ml-auto"><Icons.X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: 'Pending Requests', value: stats.pending_requests || 0, color: 'amber', icon: Icons.Clock },
          { label: 'Global Blacklist', value: stats.global_blacklist || 0, color: 'red', icon: Icons.ShieldBan },
          { label: 'Global Whitelist', value: stats.global_whitelist || 0, color: 'green', icon: Icons.ShieldCheck },
          { label: 'Country Blacklist', value: stats.country_blacklist || 0, color: 'orange', icon: Icons.Globe },
          { label: 'Country Whitelist', value: stats.country_whitelist || 0, color: 'blue', icon: Icons.Globe },
          { label: 'User Blacklist', value: stats.user_blacklist || 0, color: 'purple', icon: Icons.UserX },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase">{s.label}</p>
                <p className={`text-2xl font-bold mt-1 text-${s.color}-600`}>{s.value}</p>
              </div>
              <div className={`p-2 bg-${s.color}-50 rounded-lg`}><s.icon className={`w-5 h-5 text-${s.color}-500`} /></div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.key ? 'border-indigo-500 text-indigo-600 bg-indigo-50/50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full ${activeTab === tab.key ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{tab.count}</span>}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeTab === 'requests' && <RequestsTab showMsg={showMsg} onRefresh={fetchStats} />}
          {activeTab === 'global_blacklist' && <GlobalBlacklistTab showMsg={showMsg} onRefresh={fetchStats} />}
          {activeTab === 'global_whitelist' && <GlobalWhitelistTab showMsg={showMsg} onRefresh={fetchStats} />}
          {activeTab === 'country_rules' && <CountryRulesTab showMsg={showMsg} onRefresh={fetchStats} />}
          {activeTab === 'user_blacklist' && <UserBlacklistTab showMsg={showMsg} onRefresh={fetchStats} />}
          {activeTab === 'audit_log' && <AuditLogTab />}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// REQUESTS TAB (existing IP whitelist approval flow)
// ============================================================
const RequestsTab = ({ showMsg, onRefresh }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [actionModal, setActionModal] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ipWhitelistAdminAPI.getAllRequests(filter === 'all' ? '' : filter);
      setRequests(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const filtered = requests.filter(r =>
    !search || r.ip_address?.includes(search) || r.username?.toLowerCase().includes(search.toLowerCase()) || r.request_number?.includes(search)
  );

  const handleAction = async (action) => {
    if (!actionModal) return;
    setSubmitting(true);
    try {
      if (action === 'approve') await ipWhitelistAdminAPI.approve(actionModal.id, remarks);
      else if (action === 'reject') await ipWhitelistAdminAPI.reject(actionModal.id, remarks);
      else if (action === 'inactive') await ipWhitelistAdminAPI.updateStatus(actionModal.id, 'inactive', remarks);
      const label = action === 'inactive' ? 'deactivated' : `${action}d`;
      showMsg('success', `Request ${label} successfully`);
      setActionModal(null); setRemarks('');
      fetch(); onRefresh();
    } catch (e) { showMsg('error', e.response?.data?.error || `Failed to ${action}`); }
    finally { setSubmitting(false); }
  };

  const statusBadge = (status) => {
    const cfg = { pending: 'bg-amber-50 text-amber-700 border-amber-200', approved: 'bg-green-50 text-green-700 border-green-200', rejected: 'bg-red-50 text-red-700 border-red-200', inactive: 'bg-gray-50 text-gray-600 border-gray-300' };
    return <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${cfg[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{status?.toUpperCase()}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${filter === f ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search IP, user, request#..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Icons.Inbox className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No requests found</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50">
              {['Request #', 'User', 'IP Address', 'Type', 'Action', 'Status', 'Date', 'Actions'].map(h => (
                <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-2.5 font-mono text-xs text-indigo-600">{r.request_number}</td>
                  <td className="px-4 py-2.5 text-xs font-medium text-gray-800">{r.username || `User #${r.user_id}`}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{r.ip_address}</td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200 uppercase">{r.ip_type}</span></td>
                  <td className="px-4 py-2.5 text-xs capitalize text-gray-600">{r.action_type}</td>
                  <td className="px-4 py-2.5">{statusBadge(r.status)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2.5">
                    {r.status === 'pending' && (
                      <div className="flex gap-1">
                        <button onClick={() => { setActionModal({ ...r, action: 'approve' }); setRemarks(''); }}
                          className="px-2 py-1 text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100">Approve</button>
                        <button onClick={() => { setActionModal({ ...r, action: 'reject' }); setRemarks(''); }}
                          className="px-2 py-1 text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100">Reject</button>
                      </div>
                    )}
                    {r.status === 'approved' && (
                      <button onClick={() => { setActionModal({ ...r, action: 'inactive' }); setRemarks(''); }}
                        className="px-2 py-1 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100">Inactive</button>
                    )}
                    {r.status === 'inactive' && (
                      <button onClick={() => { setActionModal({ ...r, action: 'approve' }); setRemarks(''); }}
                        className="px-2 py-1 text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100">Re-activate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4">{{ approve: 'Approve', reject: 'Reject', inactive: 'Deactivate' }[actionModal.action] || actionModal.action} Request</h3>
            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-sm"><span className="text-gray-500">Request:</span><span className="font-mono">{actionModal.request_number}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">IP:</span><span className="font-mono">{actionModal.ip_address}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">User:</span><span>{actionModal.username}</span></div>
              <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks (optional)" rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setActionModal(null)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={() => handleAction(actionModal.action)} disabled={submitting}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${actionModal.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {submitting ? 'Processing...' : actionModal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// GLOBAL BLACKLIST TAB
// ============================================================
const GlobalBlacklistTab = ({ showMsg, onRefresh }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ip_address: '', reason: '', expires_at: '' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await ipSecurityAPI.getGlobalBlacklist(search); setEntries(res.data?.entries || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!form.ip_address) return;
    try {
      await ipSecurityAPI.addGlobalBlacklist(form);
      showMsg('success', 'IP added to global blacklist');
      setShowAdd(false); setForm({ ip_address: '', reason: '', expires_at: '' });
      fetch(); onRefresh();
    } catch (e) { showMsg('error', e.response?.data?.error || 'Failed to add'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this blacklist entry?')) return;
    try { await ipSecurityAPI.deleteGlobalBlacklist(id); showMsg('success', 'Entry deleted'); fetch(); onRefresh(); }
    catch (e) { showMsg('error', 'Failed to delete'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <p className="text-sm text-gray-500">IPs blocked globally across all users and services</p>
        <div className="flex gap-2">
          <div className="relative">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search IP or reason..."
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 w-56" />
          </div>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
            <Icons.Plus className="w-4 h-4" /> Block IP
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
          <h4 className="font-semibold text-gray-900 text-sm">Add IP to Global Blacklist</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input type="text" value={form.ip_address} onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))} placeholder="IP Address *"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-red-500" />
            <input type="text" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Reason"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500" />
            <input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} title="Expires at (optional)"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleAdd} disabled={!form.ip_address} className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">Add to Blacklist</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div> : entries.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Icons.ShieldBan className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No blacklisted IPs</p></div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="bg-gray-50">
            {['IP Address', 'Reason', 'Status', 'Expires', 'Added', 'Actions'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>{entries.map(e => (
            <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-4 py-2.5 font-mono text-xs text-gray-800">{e.ip_address}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{e.reason || '-'}</td>
              <td className="px-4 py-2.5">{e.is_active ? <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-50 text-red-700 border border-red-200">ACTIVE</span> : <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-50 text-gray-500 border border-gray-200">INACTIVE</span>}</td>
              <td className="px-4 py-2.5 text-xs text-gray-500">{e.expires_at ? new Date(e.expires_at).toLocaleDateString() : 'Never'}</td>
              <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(e.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-2.5"><button onClick={() => handleDelete(e.id)} className="text-red-500 hover:text-red-700"><Icons.Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
};

// ============================================================
// GLOBAL WHITELIST TAB
// ============================================================
const GlobalWhitelistTab = ({ showMsg, onRefresh }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ip_address: '', description: '' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await ipSecurityAPI.getGlobalWhitelist(search); setEntries(res.data?.entries || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!form.ip_address) return;
    try {
      await ipSecurityAPI.addGlobalWhitelist(form);
      showMsg('success', 'IP added to global whitelist');
      setShowAdd(false); setForm({ ip_address: '', description: '' });
      fetch(); onRefresh();
    } catch (e) { showMsg('error', e.response?.data?.error || 'Failed to add'); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this whitelist entry?')) return;
    try { await ipSecurityAPI.removeGlobalWhitelist(id); showMsg('success', 'Entry removed'); fetch(); onRefresh(); }
    catch (e) { showMsg('error', 'Failed to remove'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <p className="text-sm text-gray-500">IPs always allowed globally (bypass all checks)</p>
        <div className="flex gap-2">
          <div className="relative">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 w-56" />
          </div>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
            <Icons.Plus className="w-4 h-4" /> Allow IP
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
          <h4 className="font-semibold text-gray-900 text-sm">Add IP to Global Whitelist</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input type="text" value={form.ip_address} onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))} placeholder="IP Address *"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-green-500" />
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleAdd} disabled={!form.ip_address} className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">Add to Whitelist</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div> : entries.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Icons.ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No whitelisted IPs</p></div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="bg-gray-50">
            {['IP Address', 'Description', 'Status', 'Added', 'Actions'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>{entries.map(e => (
            <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-4 py-2.5 font-mono text-xs text-gray-800">{e.ip_address}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{e.description || '-'}</td>
              <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">ACTIVE</span></td>
              <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(e.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-2.5"><button onClick={() => handleRemove(e.id)} className="text-red-500 hover:text-red-700"><Icons.Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
};

// ============================================================
// COUNTRY RULES TAB
// ============================================================
const COUNTRIES = [
  {code:'AF',name:'Afghanistan'},{code:'AL',name:'Albania'},{code:'DZ',name:'Algeria'},{code:'AD',name:'Andorra'},{code:'AO',name:'Angola'},{code:'AG',name:'Antigua and Barbuda'},{code:'AR',name:'Argentina'},{code:'AM',name:'Armenia'},{code:'AU',name:'Australia'},{code:'AT',name:'Austria'},{code:'AZ',name:'Azerbaijan'},{code:'BS',name:'Bahamas'},{code:'BH',name:'Bahrain'},{code:'BD',name:'Bangladesh'},{code:'BB',name:'Barbados'},{code:'BY',name:'Belarus'},{code:'BE',name:'Belgium'},{code:'BZ',name:'Belize'},{code:'BJ',name:'Benin'},{code:'BT',name:'Bhutan'},{code:'BO',name:'Bolivia'},{code:'BA',name:'Bosnia and Herzegovina'},{code:'BW',name:'Botswana'},{code:'BR',name:'Brazil'},{code:'BN',name:'Brunei'},{code:'BG',name:'Bulgaria'},{code:'BF',name:'Burkina Faso'},{code:'BI',name:'Burundi'},{code:'KH',name:'Cambodia'},{code:'CM',name:'Cameroon'},{code:'CA',name:'Canada'},{code:'CV',name:'Cape Verde'},{code:'CF',name:'Central African Republic'},{code:'TD',name:'Chad'},{code:'CL',name:'Chile'},{code:'CN',name:'China'},{code:'CO',name:'Colombia'},{code:'KM',name:'Comoros'},{code:'CG',name:'Congo'},{code:'CD',name:'Congo (DRC)'},{code:'CR',name:'Costa Rica'},{code:'CI',name:"Cote d'Ivoire"},{code:'HR',name:'Croatia'},{code:'CU',name:'Cuba'},{code:'CY',name:'Cyprus'},{code:'CZ',name:'Czech Republic'},{code:'DK',name:'Denmark'},{code:'DJ',name:'Djibouti'},{code:'DM',name:'Dominica'},{code:'DO',name:'Dominican Republic'},{code:'EC',name:'Ecuador'},{code:'EG',name:'Egypt'},{code:'SV',name:'El Salvador'},{code:'GQ',name:'Equatorial Guinea'},{code:'ER',name:'Eritrea'},{code:'EE',name:'Estonia'},{code:'SZ',name:'Eswatini'},{code:'ET',name:'Ethiopia'},{code:'FJ',name:'Fiji'},{code:'FI',name:'Finland'},{code:'FR',name:'France'},{code:'GA',name:'Gabon'},{code:'GM',name:'Gambia'},{code:'GE',name:'Georgia'},{code:'DE',name:'Germany'},{code:'GH',name:'Ghana'},{code:'GR',name:'Greece'},{code:'GD',name:'Grenada'},{code:'GT',name:'Guatemala'},{code:'GN',name:'Guinea'},{code:'GW',name:'Guinea-Bissau'},{code:'GY',name:'Guyana'},{code:'HT',name:'Haiti'},{code:'HN',name:'Honduras'},{code:'HK',name:'Hong Kong'},{code:'HU',name:'Hungary'},{code:'IS',name:'Iceland'},{code:'IN',name:'India'},{code:'ID',name:'Indonesia'},{code:'IR',name:'Iran'},{code:'IQ',name:'Iraq'},{code:'IE',name:'Ireland'},{code:'IL',name:'Israel'},{code:'IT',name:'Italy'},{code:'JM',name:'Jamaica'},{code:'JP',name:'Japan'},{code:'JO',name:'Jordan'},{code:'KZ',name:'Kazakhstan'},{code:'KE',name:'Kenya'},{code:'KI',name:'Kiribati'},{code:'KP',name:'North Korea'},{code:'KR',name:'South Korea'},{code:'KW',name:'Kuwait'},{code:'KG',name:'Kyrgyzstan'},{code:'LA',name:'Laos'},{code:'LV',name:'Latvia'},{code:'LB',name:'Lebanon'},{code:'LS',name:'Lesotho'},{code:'LR',name:'Liberia'},{code:'LY',name:'Libya'},{code:'LI',name:'Liechtenstein'},{code:'LT',name:'Lithuania'},{code:'LU',name:'Luxembourg'},{code:'MO',name:'Macau'},{code:'MG',name:'Madagascar'},{code:'MW',name:'Malawi'},{code:'MY',name:'Malaysia'},{code:'MV',name:'Maldives'},{code:'ML',name:'Mali'},{code:'MT',name:'Malta'},{code:'MH',name:'Marshall Islands'},{code:'MR',name:'Mauritania'},{code:'MU',name:'Mauritius'},{code:'MX',name:'Mexico'},{code:'FM',name:'Micronesia'},{code:'MD',name:'Moldova'},{code:'MC',name:'Monaco'},{code:'MN',name:'Mongolia'},{code:'ME',name:'Montenegro'},{code:'MA',name:'Morocco'},{code:'MZ',name:'Mozambique'},{code:'MM',name:'Myanmar'},{code:'NA',name:'Namibia'},{code:'NR',name:'Nauru'},{code:'NP',name:'Nepal'},{code:'NL',name:'Netherlands'},{code:'NZ',name:'New Zealand'},{code:'NI',name:'Nicaragua'},{code:'NE',name:'Niger'},{code:'NG',name:'Nigeria'},{code:'MK',name:'North Macedonia'},{code:'NO',name:'Norway'},{code:'OM',name:'Oman'},{code:'PK',name:'Pakistan'},{code:'PW',name:'Palau'},{code:'PS',name:'Palestine'},{code:'PA',name:'Panama'},{code:'PG',name:'Papua New Guinea'},{code:'PY',name:'Paraguay'},{code:'PE',name:'Peru'},{code:'PH',name:'Philippines'},{code:'PL',name:'Poland'},{code:'PT',name:'Portugal'},{code:'QA',name:'Qatar'},{code:'RO',name:'Romania'},{code:'RU',name:'Russia'},{code:'RW',name:'Rwanda'},{code:'KN',name:'Saint Kitts and Nevis'},{code:'LC',name:'Saint Lucia'},{code:'VC',name:'Saint Vincent'},{code:'WS',name:'Samoa'},{code:'SM',name:'San Marino'},{code:'ST',name:'Sao Tome and Principe'},{code:'SA',name:'Saudi Arabia'},{code:'SN',name:'Senegal'},{code:'RS',name:'Serbia'},{code:'SC',name:'Seychelles'},{code:'SL',name:'Sierra Leone'},{code:'SG',name:'Singapore'},{code:'SK',name:'Slovakia'},{code:'SI',name:'Slovenia'},{code:'SB',name:'Solomon Islands'},{code:'SO',name:'Somalia'},{code:'ZA',name:'South Africa'},{code:'SS',name:'South Sudan'},{code:'ES',name:'Spain'},{code:'LK',name:'Sri Lanka'},{code:'SD',name:'Sudan'},{code:'SR',name:'Suriname'},{code:'SE',name:'Sweden'},{code:'CH',name:'Switzerland'},{code:'SY',name:'Syria'},{code:'TW',name:'Taiwan'},{code:'TJ',name:'Tajikistan'},{code:'TZ',name:'Tanzania'},{code:'TH',name:'Thailand'},{code:'TL',name:'Timor-Leste'},{code:'TG',name:'Togo'},{code:'TO',name:'Tonga'},{code:'TT',name:'Trinidad and Tobago'},{code:'TN',name:'Tunisia'},{code:'TR',name:'Turkey'},{code:'TM',name:'Turkmenistan'},{code:'TV',name:'Tuvalu'},{code:'UG',name:'Uganda'},{code:'UA',name:'Ukraine'},{code:'AE',name:'United Arab Emirates'},{code:'GB',name:'United Kingdom'},{code:'US',name:'United States'},{code:'UY',name:'Uruguay'},{code:'UZ',name:'Uzbekistan'},{code:'VU',name:'Vanuatu'},{code:'VA',name:'Vatican City'},{code:'VE',name:'Venezuela'},{code:'VN',name:'Vietnam'},{code:'YE',name:'Yemen'},{code:'ZM',name:'Zambia'},{code:'ZW',name:'Zimbabwe'}
];

const CountryRulesTab = ({ showMsg, onRefresh }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ country_code: '', country_name: '', rule_type: 'blacklist', reason: '' });
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await ipSecurityAPI.getCountryRules(); setEntries(res.data?.entries || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!form.country_code || !form.country_name) return;
    try {
      await ipSecurityAPI.addCountryRule(form);
      showMsg('success', `Country ${form.rule_type} rule added`);
      setShowAdd(false); setForm({ country_code: '', country_name: '', rule_type: 'blacklist', reason: '' });
      fetch(); onRefresh();
    } catch (e) { showMsg('error', e.response?.data?.error || 'Failed to add'); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this country rule?')) return;
    try { await ipSecurityAPI.removeCountryRule(id); showMsg('success', 'Rule removed'); fetch(); onRefresh(); }
    catch (e) { showMsg('error', 'Failed to remove'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <p className="text-sm text-gray-500">Block or allow traffic from specific countries</p>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
          <Icons.Plus className="w-4 h-4" /> Add Country Rule
        </button>
      </div>

      {showAdd && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
          <h4 className="font-semibold text-gray-900 text-sm">Add Country Rule</h4>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="relative sm:col-span-2">
              <input type="text" value={countrySearch || (form.country_code ? `${form.country_name} (${form.country_code})` : '')}
                onChange={e => { setCountrySearch(e.target.value); setShowCountryDropdown(true); setForm(f => ({ ...f, country_code: '', country_name: '' })); }}
                onFocus={() => setShowCountryDropdown(true)}
                placeholder="Search country name or code..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
              {showCountryDropdown && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {COUNTRIES.filter(c => {
                    const s = (countrySearch || '').toLowerCase();
                    return !s || c.name.toLowerCase().includes(s) || c.code.toLowerCase().includes(s);
                  }).slice(0, 50).map(c => (
                    <button key={c.code} type="button" onClick={() => {
                      setForm(f => ({ ...f, country_code: c.code, country_name: c.name }));
                      setCountrySearch(''); setShowCountryDropdown(false);
                    }} className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center justify-between">
                      <span>{c.name}</span>
                      <span className="font-mono text-xs text-gray-400">{c.code}</span>
                    </button>
                  ))}
                  {COUNTRIES.filter(c => { const s = (countrySearch || '').toLowerCase(); return !s || c.name.toLowerCase().includes(s) || c.code.toLowerCase().includes(s); }).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-400">No countries found</div>
                  )}
                </div>
              )}
            </div>
            <select value={form.rule_type} onChange={e => setForm(f => ({ ...f, rule_type: e.target.value }))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
              <option value="blacklist">Blacklist (Block)</option>
              <option value="whitelist">Whitelist (Allow)</option>
            </select>
            <input type="text" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Reason"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleAdd} disabled={!form.country_code || !form.country_name} className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">Add Rule</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div> : entries.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Icons.Globe className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No country rules configured</p></div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="bg-gray-50">
            {['Country', 'Code', 'Rule Type', 'Reason', 'Added', 'Actions'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>{entries.map(e => (
            <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-4 py-2.5 text-xs font-medium text-gray-800">{e.country_name}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{e.country_code}</td>
              <td className="px-4 py-2.5">
                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${e.rule_type === 'blacklist' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                  {e.rule_type === 'blacklist' ? 'BLOCKED' : 'ALLOWED'}
                </span>
              </td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{e.reason || '-'}</td>
              <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(e.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-2.5"><button onClick={() => handleRemove(e.id)} className="text-red-500 hover:text-red-700"><Icons.Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
};

// ============================================================
// USER BLACKLIST TAB
// ============================================================
const UserBlacklistTab = ({ showMsg, onRefresh }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ user_id: '', ip_address: '', reason: '' });
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await ipSecurityAPI.getUserBlacklist(search); setEntries(res.data?.entries || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetch(); }, [fetch]);

  // Search users with debounce
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) { setUserResults([]); return; }
    const timer = setTimeout(async () => {
      setUserSearching(true);
      try {
        const res = await userAPI.searchUsers(userSearch);
        const users = res.data?.users || res.data || [];
        setUserResults(Array.isArray(users) ? users : []);
        setShowUserDropdown(true);
      } catch (e) { console.error('User search failed:', e); setUserResults([]); }
      finally { setUserSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const selectUser = (user) => {
    setSelectedUser(user);
    setForm(f => ({ ...f, user_id: String(user.id) }));
    setUserSearch(`${user.username} (ID: ${user.id})`);
    setShowUserDropdown(false);
  };

  const handleAdd = async () => {
    if (!form.user_id || !form.ip_address) return;
    try {
      await ipSecurityAPI.addUserBlacklist({ ...form, user_id: parseInt(form.user_id) });
      showMsg('success', 'IP blacklisted for user');
      setShowAdd(false); setForm({ user_id: '', ip_address: '', reason: '' });
      setSelectedUser(null); setUserSearch('');
      fetch(); onRefresh();
    } catch (e) { showMsg('error', e.response?.data?.error || 'Failed to add'); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this user blacklist entry?')) return;
    try { await ipSecurityAPI.removeUserBlacklist(id); showMsg('success', 'Entry removed'); fetch(); onRefresh(); }
    catch (e) { showMsg('error', 'Failed to remove'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <p className="text-sm text-gray-500">Block specific IPs for individual users (suspicious activity)</p>
        <div className="flex gap-2">
          <div className="relative">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search IP, user..."
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 w-56" />
          </div>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
            <Icons.Plus className="w-4 h-4" /> Block User IP
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
          <h4 className="font-semibold text-gray-900 text-sm">Block IP for Specific User</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* User search dropdown */}
            <div className="relative">
              <div className="relative">
                <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input type="text" value={userSearch}
                  onChange={e => { setUserSearch(e.target.value); setSelectedUser(null); setForm(f => ({ ...f, user_id: '' })); setShowUserDropdown(true); }}
                  onFocus={() => userResults.length > 0 && setShowUserDropdown(true)}
                  placeholder="Search user by name or ID..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                {userSearching && <Icons.Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-purple-500" />}
                {selectedUser && <Icons.CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-green-500" />}
              </div>
              {showUserDropdown && userResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {userResults.map(u => (
                    <button key={u.id} onClick={() => selectUser(u)}
                      className="w-full text-left px-3 py-2 hover:bg-purple-50 flex items-center justify-between border-b border-gray-50 last:border-0">
                      <div>
                        <span className="text-sm font-medium text-gray-800">{u.username}</span>
                        {u.full_name && <span className="text-xs text-gray-500 ml-2">{u.full_name}</span>}
                      </div>
                      <span className="text-[10px] text-gray-400 font-mono">ID: {u.id}</span>
                    </button>
                  ))}
                </div>
              )}
              {showUserDropdown && userSearch.length >= 2 && !userSearching && userResults.length === 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center text-xs text-gray-400">
                  No users found
                </div>
              )}
            </div>
            <input type="text" value={form.ip_address} onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))} placeholder="IP Address *"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500" />
            <input type="text" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Reason (e.g. suspicious activity)"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowAdd(false); setSelectedUser(null); setUserSearch(''); }} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleAdd} disabled={!form.user_id || !form.ip_address} className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">Block IP</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div> : entries.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Icons.UserX className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No user-specific blacklist entries</p></div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="bg-gray-50">
            {['User', 'IP Address', 'Reason', 'Status', 'Added', 'Actions'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>{entries.map(e => (
            <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-4 py-2.5 text-xs font-medium text-gray-800">{e.username || `User #${e.user_id}`}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{e.ip_address}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{e.reason || '-'}</td>
              <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-50 text-red-700 border border-red-200">BLOCKED</span></td>
              <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(e.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-2.5"><button onClick={() => handleRemove(e.id)} className="text-red-500 hover:text-red-700"><Icons.Trash2 className="w-4 h-4" /></button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
};

// ============================================================
// AUDIT LOG TAB
// ============================================================
const AuditLogTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [targetType, setTargetType] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await ipSecurityAPI.getAuditLog(targetType, search, 200); setEntries(res.data?.entries || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [targetType, search]);

  useEffect(() => { fetch(); }, [fetch]);

  const actionColor = (action) => {
    if (action === 'ADD') return 'bg-green-50 text-green-700 border-green-200';
    if (action === 'REMOVE' || action === 'DELETE') return 'bg-red-50 text-red-700 border-red-200';
    if (action === 'APPROVED') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (action === 'REJECTED') return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          {[{ v: '', l: 'All' }, { v: 'global_blacklist', l: 'Blacklist' }, { v: 'global_whitelist', l: 'Whitelist' }, { v: 'country_rule', l: 'Country' }, { v: 'user_blacklist', l: 'User' }, { v: 'ip_request', l: 'Requests' }].map(f => (
            <button key={f.v} onClick={() => setTargetType(f.v)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${targetType === f.v ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {f.l}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search audit log..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>

      {loading ? <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div> : entries.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Icons.ScrollText className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No audit entries found</p></div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="bg-gray-50">
            {['Time', 'Action', 'Type', 'IP', 'Details', 'By'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>{entries.map(e => (
            <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
              <td className="px-4 py-2.5"><span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${actionColor(e.action)}`}>{e.action}</span></td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{e.target_type?.replace(/_/g, ' ')}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{e.ip_address || '-'}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600 max-w-xs truncate" title={e.details}>{e.details || '-'}</td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{e.performed_by_username || `#${e.performed_by}`}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
};

export default IPWhitelistAdmin;